import React, { FC, forwardRef, useState } from 'react';
import {
  Menu,
  MenuItem,
  Radio,
  ListItemText,
  MenuProps,
  MenuItemProps,
  RadioProps,
  ListItemTextProps,
} from '@mui/material';
import { HeaderButton, HeaderButtonProps } from './HeaderButton';
import { GlobeIcon } from '@/src/components/icon/stroke/GlobeIcon';

type HeaderLanguageType = string | { short?: string; full?: string };

export interface HeaderLanguageSelectProps extends HeaderButtonProps {
  /**
   * List of languages
   */
  languages?: HeaderLanguageType[];
  /**
   * Index of selected language
   * @default 0
   */
  defaultLanguage?: number;
  /**
   * Function that is triggered on language change
   * @param language - provided language
   * @param index - index of language
   */
  onSelect?: (language: HeaderLanguageType, index: number) => void;
  /**
   * Props applied to menu
   */
  menuProps?: MenuProps;
  /**
   * Props applied to each MenuItem
   */
  menuItemProps?: MenuItemProps;
  /**
   * Props applied to Radio inside MenuItem
   */
  radioProps?: RadioProps;
  /**
   * Props applied to ListItemText inside MenuIte
   */
  listItemTextProps?: ListItemTextProps;
}

/**
 * IconButton with menu selector for languages
 */
export const HeaderLanguageSelect: FC<HeaderLanguageSelectProps> = forwardRef(
  (
    {
      children,
      languages,
      onSelect,
      defaultLanguage = 0,
      menuProps,
      menuItemProps,
      radioProps,
      listItemTextProps,
      ...rest
    },
    ref,
  ) => {
    const [anchor, setAnchor] = useState(null);
    const [selected, setSelected] = useState(defaultLanguage);

    return (
      <>
        <HeaderButton
          onClick={(e) => setAnchor(e.currentTarget)}
          ref={ref}
          data-testid="HeaderLanguageSelect"
          {...rest}
        >
          {children || <GlobeIcon />}
        </HeaderButton>
        <Menu open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} {...menuProps}>
          {languages?.map((lang, idx) => {
            const isSelected = selected === idx;
            const value = typeof lang === 'string' ? lang : lang.full;

            return (
              <MenuItem
                key={value}
                selected={isSelected}
                onClick={() => {
                  onSelect?.(lang, idx);
                  setSelected(idx);
                }}
                {...menuItemProps}
              >
                <Radio checked={isSelected} tabIndex={-1} {...radioProps} />
                <ListItemText {...listItemTextProps}>{value}</ListItemText>
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  },
);
